import { webTransportManager } from "../entities/WebTransport";

interface CaptureMetadata {
  mailboxShmName: string;

  keroPid: number;

  url: string;

  width: number;

  height: number;
}

interface GpuCaptureInstance {
  captureId: string;

  stream: MediaStream;

  metadata: CaptureMetadata;

  cleanup: () => void;
}

/**
 * Browser Capture Service
 *
 * Manages browser capture lifecycle using D3D11 shared GPU textures.
 *
 * Flow:
 * 1. Sends WebTransport request to Kero to create CEF capture
 * 2. Kero creates CEF browser with on_accelerated_paint → D3D11 shared textures
 * 3. SHM mailbox publishes frame metadata (NT handle, PTS, dimensions)
 * 4. Electron main process polls mailbox, imports shared textures, sends to renderer
 * 5. Renderer receives VideoFrame via setSharedTextureReceiver → canvas → captureStream
 * 6. Audio: CEF outputs to OS audio, SystemAudioCapture picks it up
 */

export class BrowserCaptureService {
  private gpuCaptures: Map<string, GpuCaptureInstance> = new Map();

  /**
   * Request Kero backend to start a CEF capture and return GPU mailbox info.
   */

  private async requestKeroCapture(
    id: string,

    url: string,

    width: number,

    height: number,
  ): Promise<{
    mailboxShmName: string;

    keroPid: number;
  }> {
    let transport = webTransportManager.getTransport();

    if (!transport) {
      await webTransportManager.connect();

      transport = webTransportManager.getTransport();
    }

    if (!transport) {
      throw new Error(
        "WebTransport connection failed - Kero backend may not be running",
      );
    }

    const bidiStream = await transport.createBidirectionalStream();

    const writer = bidiStream.writable.getWriter();

    const reader = bidiStream.readable.getReader();

    const message = JSON.stringify({
      type: "capture_request",

      id,

      url,

      width,

      height,
    });

    await writer.write(new TextEncoder().encode(message));

    await writer.close();

    const timeoutPromise = new Promise<{ done: true; value: undefined }>(
      (resolve) =>
        setTimeout(() => resolve({ done: true, value: undefined }), 10000),
    );

    const result = await Promise.race([reader.read(), timeoutPromise]);

    if (result.done || !result.value) {
      throw new Error("No response from Kero backend");
    }

    const response = new TextDecoder().decode(result.value);

    reader.releaseLock();

    const json = JSON.parse(response);

    if (json.error) throw new Error(`Kero error: ${json.error}`);

    if (!json.mailbox_shm_name || !json.kero_pid) {
      throw new Error("Kero response missing mailbox_shm_name or kero_pid");
    }

    return {
      mailboxShmName: json.mailbox_shm_name,

      keroPid: json.kero_pid,
    };
  }

  /**
   * Create a GPU-resident browser capture using D3D11 shared textures (NO CPU readback).
   *
   * Flow: main process (sharedTextureHandlers) poll_frame → importSharedTexture →
   *       sendSharedTexture({frame: mainFrame}, captureId) → el receiver del preload hace
   *       getVideoFrame() y dibuja al canvas #<captureId> → captureStream → <video> → Pixi.
   * Antes esto lo hacía un Web Worker con readPixelsInto (GPU→CPU→GPU): ese readback se eliminó.
   */

  async createCapture(
    id: string,

    url: string,

    width: number,

    height: number,
  ): Promise<{ captureId: string; stream: MediaStream }> {
    const { mailboxShmName, keroPid } = await this.requestKeroCapture(
      id,

      url,

      width,

      height,
    );

    // Wait for Kero to create shared memory

    await new Promise((resolve) => setTimeout(resolve, 200));

    const electronAPI = window.electronAPI;

    if (!electronAPI?.startSharedTexture) {
      throw new Error("startSharedTexture not available in electronAPI");
    }

    // Create canvas for captureStream inside #area (same as other sources)

    const canvas = document.createElement("canvas");

    canvas.width = width;

    canvas.height = height;

    canvas.style.display = "none";

    canvas.id = `${id}`;

    const container = document.querySelector("#area");

    if (!container) {
      const wrapper = document.createElement("div");

      wrapper.id = "area";

      document.body.appendChild(wrapper);

      wrapper.appendChild(canvas);
    } else {
      container.appendChild(canvas);
    }

    const ctx = canvas.getContext("2d", { alpha: true });

    if (!ctx) {
      throw new Error("Failed to get 2d context on renderer canvas");
    }

    ctx.clearRect(0, 0, width, height);

    const stream = canvas.captureStream(60);

    // GPU-resident: el main process abre el TextureMailboxReader y empuja cada frame como shared
    // texture al renderer; el receiver del preload hace getVideoFrame() y lo dibuja a este canvas
    // (#<captureId>) → captureStream → <video> → Pixi. Sin worker, sin readback GPU→CPU.
    const startRes = await electronAPI.startSharedTexture({
      captureId: id,

      mailboxShmName,

      keroPid,
    });

    if (!startRes?.success) {
      canvas.remove();

      throw new Error(
        `startSharedTexture failed: ${startRes?.error ?? "unknown"}`,
      );
    }

    const cleanup = () => {
      void window.electronAPI?.stopSharedTexture?.(id);

      canvas.remove();
    };

    const metadata: CaptureMetadata = {
      mailboxShmName,

      keroPid,

      url,

      width,

      height,
    };

    this.gpuCaptures.set(id, {
      captureId: id,

      stream,

      metadata,

      cleanup,
    });

    return { captureId: id, stream };
  }

  /**
   * Get capture metadata.
   */

  getCaptureInfo(captureId: string): CaptureMetadata | undefined {
    return this.gpuCaptures.get(captureId)?.metadata;
  }

  /**
   * Close a browser capture (GPU teardown + Kero notification).
   */

  async close(captureId: string): Promise<void> {
    const gpu = this.gpuCaptures.get(captureId);

    if (gpu) {
      gpu.cleanup(); // stopSharedTexture (detiene el send loop del main) + remueve el canvas

      gpu.stream.getTracks().forEach((t) => t.stop());

      this.gpuCaptures.delete(captureId);
    }

    // Notify Kero

    const transport = webTransportManager.getTransport();

    if (transport) {
      try {
        const bidiStream = await transport.createBidirectionalStream();

        const writer = bidiStream.writable.getWriter();

        await writer.write(
          new TextEncoder().encode(
            JSON.stringify({ type: "capture_close", id: captureId }),
          ),
        );

        await writer.close();
      } catch {
        // Kero close notification failed — non-critical
      }
    }
  }

  /**
   * Singleton
   */

  private static instance: BrowserCaptureService | null = null;

  static getInstance(): BrowserCaptureService {
    if (!BrowserCaptureService.instance) {
      BrowserCaptureService.instance = new BrowserCaptureService();
    }

    return BrowserCaptureService.instance;
  }
}

export const browserCaptureService = BrowserCaptureService.getInstance();
